import Logger from "../core/internal/logger"
import HackleClient from "../core/HackleClient"
import HackleCore from "../core/HackleCore"
import { HackleUserResolver } from "./user/index.node"
import {
  Decision,
  DecisionReason,
  FeatureFlagDecision,
  HackleEvent,
  HackleRemoteConfig,
  User,
  VariationKey
} from "../core/internal/model/model"
import { DEFAULT_ON_READY_TIMEOUT } from "../config"
import HackleRemoteConfigImpl from "./remoteconfig/index.node"
import { TimerSample } from "../core/internal/metrics/Timer"
import { DecisionMetrics } from "../core/internal/metrics/monitoring/MonitoringMetricRegistry"
import { Metrics } from "../core/internal/metrics/Metrics"
import { PropertyOperations } from "./property/PropertyOperations"
import { PollingSynchronizer } from "../core/internal/sync/PollingSynchronizer"
import { Promises } from "../core/internal/util/Promises"
import { HackleExperiment } from "../public/model"
import { HackleSubscriptionOperations } from "./subscription/HackleSubscriptionOperations"

const log = Logger.log

export default class HackleClientImpl implements HackleClient {
  private readonly initialization: Promise<void>
  private initialized: boolean = false

  constructor(
    private readonly core: HackleCore,
    private readonly synchronizer: PollingSynchronizer,
    private readonly hackleUserResolver: HackleUserResolver
  ) {
    // This is to prevent the "This Binding" problem.
    this.initialize = this.initialize.bind(this)
    this.updateUserProperties = this.updateUserProperties.bind(this)
    this.updatePushSubscriptions = this.updatePushSubscriptions.bind(this)
    this.updateSmsSubscriptions = this.updateSmsSubscriptions.bind(this)
    this.updateKakaoSubscriptions = this.updateKakaoSubscriptions.bind(this)
    this.variation = this.variation.bind(this)
    this.variationDetail = this.variationDetail.bind(this)
    this.isFeatureOn = this.isFeatureOn.bind(this)
    this.featureFlagDetail = this.featureFlagDetail.bind(this)
    this.track = this.track.bind(this)
    this.remoteConfig = this.remoteConfig.bind(this)
    this.onReady = this.onReady.bind(this)
    this.onInitialized = this.onInitialized.bind(this)
    this.close = this.close.bind(this)
    this.getExperiment = this.getExperiment.bind(this)

    this.initialization = this.initialize()
  }

  private async initialize(): Promise<void> {
    try {
      log.debug("HackleClient initializing")
      await this.synchronizer.sync()
      log.debug(`HackleClient initialized`)
    } catch (e) {
      log.error(`HackleClient initialize failed: ${e}`)
      throw e
    } finally {
      this.initialized = true
    }
  }

  variation(experimentKey: number, user: User | string, defaultVariation: VariationKey = "A"): VariationKey {
    return this.variationDetail(experimentKey, user, defaultVariation).variation
  }

  variationDetail(experimentKey: number, user: User | string, defaultVariation: string = "A"): Decision {
    const sample = TimerSample.start()
    const decision = (function (_this: HackleClientImpl) {
      try {
        const hackleUser = _this.hackleUserResolver.resolveOrNull(user)
        if (!hackleUser) {
          return Decision.of(defaultVariation, DecisionReason.INVALID_INPUT)
        }
        return _this.core.experiment(experimentKey, hackleUser, defaultVariation)
      } catch (e) {
        log.error(
          `Unexpected exception while deciding variation for experiment[${experimentKey}]. Returning default variation[${defaultVariation}] : ${e}`
        )
        return Decision.of(defaultVariation, DecisionReason.EXCEPTION)
      }
    })(this)

    DecisionMetrics.experiment(sample, experimentKey, decision)
    return decision
  }

  isFeatureOn(featureKey: number, user: User | string): boolean {
    return this.featureFlagDetail(featureKey, user).isOn
  }

  featureFlagDetail(featureKey: number, user: User | string): FeatureFlagDecision {
    const sample = TimerSample.start()
    const decision = (function (_this: HackleClientImpl) {
      try {
        const hackleUser = _this.hackleUserResolver.resolveOrNull(user)
        if (!hackleUser) {
          return FeatureFlagDecision.off(DecisionReason.INVALID_INPUT)
        }
        return _this.core.featureFlag(featureKey, hackleUser)
      } catch (e) {
        log.error(`"Unexpected exception while deciding feature flag[${featureKey}]. Returning default value[false]`)
        return FeatureFlagDecision.off(DecisionReason.EXCEPTION)
      }
    })(this)

    DecisionMetrics.featureFlag(sample, featureKey, decision)
    return decision
  }

  track(event: string | HackleEvent, user: User | string) {
    const hackleUser = this.hackleUserResolver.resolveOrNull(user)
    if (!hackleUser) {
      log.debug(`invalid user: [${user}]`)
      return
    }
    if (typeof event === "string") {
      this.core.track({ key: event }, hackleUser)
    } else {
      this.core.track(event, hackleUser)
    }
    log.debug(`track event : ${JSON.stringify(event)}`)
  }

  remoteConfig(user: User): HackleRemoteConfig {
    return new HackleRemoteConfigImpl(this.core, this.hackleUserResolver, user)
  }

  updateUserProperties(operations: PropertyOperations, user: User) {
    try {
      const event = operations.toEvent()
      this.track(event, user)
      this.core.flush(false)
    } catch (e) {
      log.error(`Unexpected exception while update user properties: ${e}`)
    }
  }

  updatePushSubscriptions(operations: HackleSubscriptionOperations, user: User) {
    try {
      const event = operations.toEvent("$push_subscriptions")
      this.track(event, user)
      this.core.flush(false)
    } catch (e) {
      log.error(`Unexpected exception while update push subscriptions: ${e}`)
    }
  }

  updateSmsSubscriptions(operations: HackleSubscriptionOperations, user: User) {
    try {
      const event = operations.toEvent("$sms_subscriptions")
      this.track(event, user)
      this.core.flush(false)
    } catch (e) {
      log.error(`Unexpected exception while update sms subscriptions: ${e}`)
    }
  }

  updateKakaoSubscriptions(operations: HackleSubscriptionOperations, user: User) {
    try {
      const event = operations.toEvent("$kakao_subscriptions")
      this.track(event, user)
      this.core.flush(false)
    } catch (e) {
      log.error(`Unexpected exception while update kakao subscriptions: ${e}`)
    }
  }

  getExperiment(experimentKey: number): HackleExperiment | undefined {
    try {
      return this.core.getExperiment(experimentKey)
    } catch (e) {
      log.error(`"Unexpected exception while get experiment[${experimentKey}]`)
      return undefined
    }
  }

  onReady(block: () => void, timeout: number = DEFAULT_ON_READY_TIMEOUT): void {
    this.onInitialized({ timeout }).finally(() => block())
  }

  async onInitialized(config?: { timeout?: number }): Promise<{ success: boolean }> {
    if (this.initialized) {
      return { success: true }
    }

    try {
      await Promises.timeout(this.initialization, config?.timeout ?? DEFAULT_ON_READY_TIMEOUT)
      return { success: true }
    } catch (e) {
      log.error(`Failed to initialize: ${e}`)
      return { success: false }
    }
  }

  close(): void {
    log.debug("HackleClient closing")
    this.core.close()
    this.synchronizer.close()
    Metrics.globalRegistry.close()
  }
}
